<script setup>
import { debounce } from 'lodash';
import { computed, defineProps, inject, ref } from 'vue';
import { useStore } from 'vuex';
import { pushToDataLayer } from '@/ui/scripts/analytics/analytics.methods';
import { BaseButton, Listbox, ListboxGroup, ListboxOption } from '@/app/components';

const props = defineProps({
  isBannerLayout: {
    default: false,
    required: false,
  },
});

const modifiers = computed(() => {
  return { 'c-project-search--banner-layout': props.isBannerLayout };
});

const { projectSearchCtaInput, projectSearchFormAction } = inject('config');
const store = useStore();
const currentSelectionIndex = ref(-1);
const listboxId = ref('project-search-suggestions');
const listViewIsAToZ = computed(() => store.state.projectsMap.listViewIsAToZ);
const hasLocations = computed(() => store.getters['projectsMap/hasLocations']);
const hasProjects = computed(() => store.getters['projectsMap/hasProjects']);
const hasSuggestions = computed(() => store.getters['projectsMap/hasSuggestions']);
const isListView = computed(() => store.getters['projectsMap/isListView']);
const magicKey = computed(() => store.state.projectsMap.selectedLocation.magicKey);
const setSize = computed(() => suggestedProjects.value.length + suggestedLocations.value.length);
const suggestedLocations = computed(() => store.state.projectsMap.suggestedLocations);
const suggestedProjects = computed(() => store.state.projectsMap.suggestedProjects);
const suggestionsModel = computed({
  get() {
    return store.state.projectsMap.suggestionsModel;
  },
  set(value) {
    store.commit(`projectsMap/SET_SUGGESTIONS_MODEL`, value);
    store.dispatch(`projectsMap/resetSelectedLocation`);
    if (value === '') {
      store.dispatch(`projectsMap/resetSuggestions`);
    }
  },
});
const activeDescendant = computed(() => {
  if (currentSelectionIndex.value > -1) {
    return `option__${currentSelectionIndex.value}`;
  }
  return null;
});

// GTM: start
const gtmEventProps = computed(() => {
  return {
    prefix: props.isBannerLayout ? 'projectMapSearch' : 'projectSearch',
  };
});

function gtmSearchTrigger(searchValue) {
  let value = 'empty input';
  if (searchValue && typeof searchValue === 'string') {
    value = searchValue;
  } else if (suggestionsModel.value !== '') {
    value = suggestionsModel.value;
  }
  pushToDataLayer(
    `${gtmEventProps.value.prefix}_tracking`,
    `${gtmEventProps.value.prefix}_action`,
    `${gtmEventProps.value.prefix}_label`,
    `Click ${value} - Search`,
    projectSearchFormAction,
  );
}

function gtmComboBoxTrigger(option) {
  pushToDataLayer(
    `${gtmEventProps.value.prefix}ComboBox_tracking`,
    `${gtmEventProps.value.prefix}ComboBox_action`,
    `${gtmEventProps.value.prefix}ComboBox_label`,
    `Click - Combo Box`,
    option,
  );
}
// GTM: end

function decrementSelectionIndex() {
  if (currentSelectionIndex.value > -1) {
    currentSelectionIndex.value--;
  } else {
    currentSelectionIndex.value = setSize.value - 1;
  }
}

function enterHandler(event) {
  if (currentSelectionIndex.value > -1) {
    event.preventDefault();
    setSelection(suggestedLocations.value[currentSelectionIndex.value]);
  }
}

function incrementSelectionIndex() {
  if (currentSelectionIndex.value < setSize.value - 1) {
    currentSelectionIndex.value++;
  } else {
    currentSelectionIndex.value = -1;
  }
}

function locationClickHandler(location) {
  setSelection(location);
}

function fetchSuggestions(event) {
  if (event.keyCode !== 38 && event.keyCode !== 40 && event.keyCode !== 13) {
    if (suggestionsModel.value) {
      if (isListView.value) {
        store.dispatch('projectsMap/filterSuggestions', suggestionsModel.value);
      } else {
        store.dispatch(`projectsMap/fetchSuggestions`, suggestionsModel.value);
      }
    } else {
      store.dispatch('projectsMap/resetSuggestions');
    }
  }
}

function projectClickHandler(project) {
  gtmComboBoxTrigger(project.name);
  gtmSearchTrigger(project.name);
  window.location = project.url;
}

function setSelection(value) {
  gtmComboBoxTrigger(value.text);
  store.commit(`projectsMap/SET_SELECTED_LOCATION`, value);
  store.commit(`projectsMap/SET_SUGGESTIONS_MODEL`, value.text);
  currentSelectionIndex.value = -1;
  store.dispatch('projectsMap/resetSuggestions');
}

const keyupHandler = debounce((event) => {
  fetchSuggestions(event);
}, 200);

const vClickOutside = {
  mounted: function (el, binding) {
    el.clickOutsideEvent = function (event) {
      if (!(el == event.target || el.contains(event.target))) {
        binding.value(event, el);
      }
    };
    document.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted: function (el) {
    document.removeEventListener('click', el.clickOutsideEvent);
  },
};

function onClickOutside() {
  store.dispatch('projectsMap/resetSuggestions');
}
</script>

<template>
  <form :action="projectSearchFormAction" :class="modifiers" class="c-project-search" method="GET">
    <div class="c-project-search__wrapper">
      <h2 id="projectSearchTitle" class="c-project-search__label">
        {{ projectSearchCtaInput.label }}
      </h2>
      <div class="c-project-search__field" v-click-outside="onClickOutside">
        <div class="c-project-search__input-wrapper">
          <input
            v-model="suggestionsModel"
            @keydown.enter="enterHandler"
            @keydown.down.prevent="incrementSelectionIndex"
            @keydown.up.prevent="decrementSelectionIndex"
            @keyup="keyupHandler"
            aria-labelledby="projectSearchTitle"
            :aria-activedescendant="activeDescendant"
            :aria-expanded="hasSuggestions"
            :aria-owns="listboxId"
            :disabled="listViewIsAToZ"
            :placeholder="projectSearchCtaInput.placeholder"
            aria-autocomplete="list"
            autocomplete="off"
            class="c-project-search__input"
            data-qa="searchTerm"
            id="searchTerm"
            name="searchTerm"
            role="combobox"
            type="text"
          />
          <input :value="magicKey" data-qa="mk" name="mk" type="hidden" />
          <BaseButton
            :disabled="listViewIsAToZ"
            :modifiers="['icon-only', 'icon', 'in-field']"
            icon="#search"
            type="submit"
            @click="gtmSearchTrigger"
          >
            <span class="visually-hidden">Search</span>
          </BaseButton>
        </div>
        <Listbox v-if="hasSuggestions" :id="listboxId" @focusout="onClickOutside">
          <ListboxGroup v-if="hasProjects">
            <template v-slot:category>Projects</template>
            <template v-slot:options>
              <ListboxOption
                v-for="(project, projectIndex) in suggestedProjects"
                @click="projectClickHandler(project)"
                :currentIndex="currentSelectionIndex"
                :index="projectIndex"
                :key="project.url"
                :setSize="setSize"
                :startingIndex="suggestedLocations.length"
              >
                {{ project.name }}
              </ListboxOption>
            </template>
          </ListboxGroup>
          <ListboxGroup v-if="hasLocations">
            <template v-slot:category>Locations</template>
            <template v-slot:options>
              <ListboxOption
                v-for="(location, locationIndex) in suggestedLocations"
                @click="locationClickHandler(location)"
                :currentIndex="currentSelectionIndex"
                :index="locationIndex"
                :key="location.magicKey"
                :setSize="setSize"
              >
                {{ location.text }}
              </ListboxOption>
            </template>
          </ListboxGroup>
        </Listbox>
      </div>
    </div>
    <div class="c-project-search__actions">
      <slot />
    </div>
  </form>
</template>

<style lang="scss">
@import 'sass-mq';
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/mixins/fonts';
@import '@/ui/styles/mixins/forms';
@import '@/ui/styles/mixins/pseudo';
@import '@/ui/styles/mixins/motifs';
@import '@/ui/styles/settings/breakpoints';
@import '@/ui/styles/settings/colors';
@import '@/ui/styles/settings/fonts';
@import '@/ui/styles/settings/units';

.c-project-search-cta-contain:not(:first-child) {
  margin: $spacing-xx-large auto 0;
}

.c-project-search {
  position: relative;
  width: 100%;
  background-color: $color-brand-one;
  border-radius: rem(30px) 0;
  color: $white;

  // bg image ratio thinner than others on site
  &:after {
    @include pseudo(100%, var(--vw, 100vw));
    @include gridlines-64-11-bg-image;
    z-index: -1;
    margin-left: 50%;
    height: 200%;
    width: var(--vw, 100vw);
    background-position: center;
    background-repeat: no-repeat;
    background-size: var(--vw, 100vw);
    transform: translate(-50%, -25%);
  }

  &--banner-layout {
    margin: 0 auto;
    background-color: $color-brand-one;
    border-radius: 0;

    &:after {
      display: none;
      background-image: none;
    }

    @include mq($from: large) {
      display: flex;
      padding-right: $page-side-padding;
      padding-left: $page-side-padding;
      background-color: transparent;
    }
  }
}

.c-project-search__wrapper {
  flex-grow: 1;
  margin: 0 auto rem(28px);
  padding: $spacing-large $spacing-large 0;

  .c-project-search--banner-layout & {
    margin-bottom: rem(28px);
    padding: $spacing-large rem(10px) 0;

    @include mq($from: large) {
      display: flex;
      flex: 1 0 max-content;
      margin-bottom: 0;
      padding: 0;
      max-width: none;
      min-width: 50%;
    }
  }
}

.c-project-search__field {
  @include field-wrapper;
  position: relative;
  margin: 0 auto;
  max-width: rem(480px);

  .c-project-search--banner-layout & {
    @include mq($from: large) {
      display: flex;
      flex: 1;
      align-items: center;
      max-width: none;
    }
  }
}

.c-project-search__label {
  @include h2-style;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: $spacing-medium;
  color: $white;
  text-align: center;

  .c-project-search--banner-layout & {
    @include mq($from: large) {
      margin: 0 rem(30px) 0 0;
    }
  }
}

.c-project-search__input-wrapper {
  @include input-wrapper(none);
  position: relative;
}

input.c-project-search__input {
  @include body-style;
  padding: rem(18px) $spacing-xxx-large rem(18px) rem(18px);
  max-width: none;
  border: 0;
  box-shadow: 0 rem(4px) rem(4px) rgba($black, 0.25);
  color: $color-text-form-placeholder;
}

.c-project-search__form-cta {
  position: absolute;
  top: rem(10px);
  right: rem(10px);
  height: rem(40px);
  width: rem(40px);
  background-color: $color-brand-three;
  border: 0;
  border-radius: $border-radius-small;
  color: $white;

  .c-button__icon {
    max-width: rem(20px);
    max-height: rem(20px);
  }
}

.c-project-search__actions {
  @include h4-style;
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-medium $spacing-large;
  justify-content: center;
  align-items: center;
  padding: $spacing-large;
  background-color: $color-brand-two;
  border-radius: 0 0 rem(30px) 0;

  .c-project-search--banner-layout & {
    flex-wrap: nowrap;
    padding: rem(18px) rem(10px);
    border-radius: 0;

    button {
      min-width: unset;
    }

    @include mq($from: large) {
      margin-left: $spacing-x-large;
      padding: 0;
    }
  }
}
</style>
